'use client';

import React, { Suspense, useEffect } from 'react';
import { AppRouterCacheProvider } from '@mui/material-nextjs/v13-appRouter';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { theme } from '@racefriends/shared/theme';
import { initializeAuthStore } from '@/lib/auth';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NuqsAdapter } from 'nuqs/adapters/next/app';
import SentryInit from './components/SentryInit';
import { FullscreenProvider } from './app/contexts/FullscreenContext';
import PostHogInit from './components/PostHogInit';
import PostHogPageView from './components/PostHogPageView';
import { NavProvider } from '@racefriends/shared/nav';
import { webNavAdapter } from '@/lib/nav-adapter';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data is considered fresh
      gcTime: 10 * 60 * 1000, // 10 minutes - cached data is kept
      refetchOnWindowFocus: true, // refetch when user returns to tab
      refetchOnMount: true, // always check for fresh data on mount
    },
  },
});

export default function Providers({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const init = async () => {
      await initializeAuthStore();
    };
    init();
  }, []);

  // Service Worker disabled - was causing 404 errors on JS chunks
  // useEffect(() => {
  //   if ('serviceWorker' in navigator) {
  //     navigator.serviceWorker
  //       .register('/sw.js')
  //       .catch((err) => console.error('SW registration failed', err));
  //     // TODO: handle service worker updates and offline fallback UI
  //   }
  // }, []);

  // Unregister any existing service workers to fix chunk loading issues
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          registration.unregister().then((success) => {
            if (success) {
              console.log('Service Worker unregistered successfully');
            }
          });
        });
      });
    }
  }, []);

  return (
    <AppRouterCacheProvider>
      <ThemeProvider theme={theme}>
        <QueryClientProvider client={queryClient}>
          <NuqsAdapter>
            <NavProvider adapter={webNavAdapter}>
              <FullscreenProvider>
                <CssBaseline />
                <SentryInit />
                {/* Must stay above PostHogPageView: sibling effects run in
                    tree order, so the SDK is initialized before the first
                    $pageview is captured. */}
                <PostHogInit />
                <Suspense fallback={null}>
                  <PostHogPageView />
                </Suspense>
                {/* Any screen reading a query string goes through the nav
                    adapter's useSearchParams, which opts that page out of
                    prerendering and *requires* a boundary above it — without
                    one the export throws and the whole build fails, as it did
                    on /mobile/home. One boundary here covers every route, so
                    adding a query param to a screen can never break the build
                    again. */}
                <Suspense fallback={null}>{children}</Suspense>
              </FullscreenProvider>
            </NavProvider>
          </NuqsAdapter>
        </QueryClientProvider>
      </ThemeProvider>
    </AppRouterCacheProvider>
  );
}
